import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  KnowledgeBase,
  KnowledgeBaseCreate,
  KnowledgeBaseUpdate,
  KnowledgeBaseWithCount,
} from "@/api/types";

const KEY = ["knowledge-bases"] as const;

interface UseKbsFilters {
  include_inactive?: boolean;
}

export function useKbs(filters: UseKbsFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () =>
      api.GET<KnowledgeBaseWithCount[]>("/knowledge-bases", {
        params: filters.include_inactive ? { include_inactive: true } : {},
      }),
  });
}

export function useKb(kbId: string) {
  return useQuery({
    queryKey: [...KEY, kbId],
    queryFn: () => api.GET<KnowledgeBase>(`/knowledge-bases/${kbId}`),
    enabled: Boolean(kbId),
  });
}

export function useCreateKb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: KnowledgeBaseCreate) =>
      api.POST<KnowledgeBase>("/knowledge-bases", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateKb(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: KnowledgeBaseUpdate) =>
      api.PATCH<KnowledgeBase>(`/knowledge-bases/${kbId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteKb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kbId: string) => api.DELETE(`/knowledge-bases/${kbId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
